import {useCallback, useEffect, useRef, useState} from 'react';
import dayjs from 'dayjs';
import {SECONDS_IN_MINUTE, MINUTES_IN_HOUR, SECONDS_IN_HOUR} from '../constants/constants';

type ResultTimeProps = {
  hours: number,
  minutes: number,
  seconds: number
}

type TimerProps = {
  initialDiff?: number,
  initialActive?: boolean,
}

type TimerParams = {
  isActive: boolean,
  stopTimer: () => void,
  startTimer: () => void,
  resultTimeHHMMSS: string,
  timeDiffInSeconds: number,
  hours: number,
  minutes: number,
  seconds: number
}

const formatTimeUnit = (unit: number): string => {
  if (!unit) {
    return `00`;
  }

  if (unit < 10) {
    return `0${unit}`;
  }

  return `${unit}`;
};

const getResultTimeHHMMSS = ({
  hours, minutes, seconds
}: ResultTimeProps): string => {
  const formedHours = formatTimeUnit(hours);
  const formedMinutes = formatTimeUnit(minutes);
  const formedSeconds = formatTimeUnit(seconds);

  return `${formedHours}:${formedMinutes}:${formedSeconds}`;
};

export const getDatesDifferenceInSeconds = (
    startDate: Nullable<string> = null,
    endDate: Nullable<string> = null
): number => {
  const definedEndDate = endDate ? dayjs(endDate) : dayjs();
  const definedStartDate = startDate ? dayjs(startDate) : definedEndDate;

  return definedEndDate.diff(definedStartDate, 'seconds');
};

const useTimer = ({
  initialDiff = 0, initialActive = false
}: TimerProps): TimerParams => {
  const [isActive, setIsActive] = useState(initialActive);
  const [diffInSeconds, setDiffInSeconds] = useState<number>(initialDiff);

  const intervalRef = useRef<Nullable<NodeJS.Timeout>>(null);

  const stopTimer = useCallback(() => {
    if (!isActive) {
      return;
    }

    setIsActive(false);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  }, [isActive, intervalRef?.current]);

  const startTimer = useCallback(() => {
    if (!isActive) {
      setIsActive(true);
    }
  }, [isActive]);

  const updateTimer = useCallback(() => {
    setDiffInSeconds((prevState) => prevState + 1);
  }, []);

  useEffect(() => {
    if (!!initialDiff) {
      setDiffInSeconds(initialDiff);
    }
  }, [initialDiff]);

  useEffect(() => {
    if (isActive) {
      intervalRef.current = setInterval(updateTimer, 1000);
    }

    return () => {
      if (intervalRef.current) {
        return clearInterval(intervalRef.current);
      }
    };
  }, [isActive, updateTimer]);

  const hours = Math.floor(diffInSeconds / SECONDS_IN_HOUR);
  const minutes = Math.floor(
      diffInSeconds / SECONDS_IN_MINUTE - hours * MINUTES_IN_HOUR
  );
  const seconds = Math.floor(
      diffInSeconds - hours * SECONDS_IN_HOUR - minutes * SECONDS_IN_MINUTE
  );

  const resultTimeHHMMSS = getResultTimeHHMMSS({hours, minutes, seconds});

  return {
    isActive,
    stopTimer,
    startTimer,
    resultTimeHHMMSS,
    timeDiffInSeconds: diffInSeconds,
    hours,
    minutes,
    seconds
  };
};

export default useTimer;
